import { faCloudUpload } from '@fortawesome/pro-regular-svg-icons';
import {
  Body,
  Button,
  Divider,
  Input,
  LargeTitle,
  LegalBody,
  Select,
  Textarea,
  TinyTitle,
  useFlashMessage
} from '@indigo-ag/component-module';
import { AgFinancingDocumentType, AgFinancingFile, AgFinancingFilesContext, ContractStatus } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { Dictionary } from 'lodash';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { DraftData, ProgressDictionary } from 'features/brazil-grower/components/BrazilGrowerFinance/utils';
import { GuaranteeData } from 'features/brazil-grower/components/shared/GuaranteeData/GuaranteeData';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  AttachButton,
  DocumentCard,
  PhaseSection,
  UploadDocumentCard,
  documentRequestTypesOptions,
  documentRequestTypesTranslations,
  utils
} from 'shared';
import { UserUtil, date } from 'shared/utils';
import { CollateralFormalizationContext } from '../../CollateralAnalysis/CollateralFormalization/CollateralFormalization';

const StyledSelect = styled(Select)`
  margin-bottom: 24px;
  margin-right: 20px;
  max-height: 54px;
  width: 180px;
`;

interface DraftProps {
  data: DraftData;
}

export const Draft: React.FunctionComponent<DraftProps> = ({ data }) => {
  const { contractStatus, draftInstructions, draftRegistrationNumber, draftFiles, draftSentBy, draftSentAt } = data;
  /**
   * State
   */
  const [sendingDraft, setSendingDraft] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');
  const [registrationNumberInputError, setRegistrationNumberInputError] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<ProgressDictionary>();
  const [documentTypeDictionary, setUpdateDocumentType] = useState<Dictionary<AgFinancingDocumentType>>({});

  /**
   * Context
   */
  const { updatePhase3, uploadFilesToPhase3 } = useContext(CollateralFormalizationContext);
  const { agFinance } = useContext(FinanceProcessContext);

  const { addFlashMessage } = useFlashMessage();

  const handleDraftNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setDraftNotes(event.target.value);
  };

  const handleRegistrationNumber = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRegistrationNumber(event.target.value);
  };

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const handleFileRemove = (fileId: string) => {
    setSelectedFiles(selectedFiles?.filter(f => f['newFileId'] !== fileId));
  };

  const handleUpdateDocumentType = (fileId: string) => {
    return (newDocumentType: string | null) => {
      if (newDocumentType) {
        setUpdateDocumentType({ ...documentTypeDictionary, [fileId]: newDocumentType as AgFinancingDocumentType });
      }
    };
  };

  const validateInputs = (): boolean => {
    setRegistrationNumberInputError('');
    if (!registrationNumber) {
      setRegistrationNumberInputError(i18next.t('inputFieldRequiredError'));
      return false;
    }
    return true;
  };

  const handleSendDraft = () => {
    if (!validateInputs()) return;
    setSendingDraft(true);
    uploadFilesToPhase3(
      selectedFiles,
      AgFinancingFilesContext.CONTRACT_DRAFT_DOCUMENTS,
      documentTypeDictionary,
      percentCompleted => setProgress({ ...percentCompleted })
    )
      .then(filesUploaded =>
        updatePhase3(
          {
            draftInstructions: draftNotes,
            draftRegistrationNumber: registrationNumber,
            contractStatus: ContractStatus.DRAFT_SENT_TO_COMMERCIAL
          },
          filesUploaded.fulfilled as AgFinancingFile[]
        )
      )
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('successSendingCPR')}`,
          title: '',
          variant: 'success'
        });
      })
      .catch(error => {
        addFlashMessage({
          content: `${i18next.t('error')}`,
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setSendingDraft(false);
      });
  };
  const amountOfOpportunities = agFinance?.orderInfo?.opportunities.length || 0;
  const editable = contractStatus === ContractStatus.WAITING_DRAFT;
  const isSendDraftDisabled = !!(
    !selectedFiles?.length || selectedFiles?.some(f => !documentTypeDictionary[f['newFileId']])
  );

  return (
    <PhaseSection title={i18next.t('draftCreation')} subtitle={i18next.t('draftCreationSubtitle')}>
      {editable && (
        <>
          <Input
            label={`${utils.text.firstLetterUppercase(i18next.t('draftNumber'))}`}
            sizeVariant="slim"
            value={registrationNumber}
            error={registrationNumberInputError}
            onChange={handleRegistrationNumber}
          />
          <Textarea
            label={`${i18next.t('requirements')} (${i18next.t('optional')})`}
            rows={4}
            value={draftNotes}
            onChange={handleDraftNotesChange}
          />
        </>
      )}
      {!!amountOfOpportunities
        ? agFinance?.orderInfo?.opportunities.map(({ collateralDueDate, tradingName }, index) => (
            <div key={index} className="flex">
              <div className="flex flex-column mb-2 mr-3">
                <LegalBody>{i18next.t('opportunityName')}</LegalBody>
                <Body className="mt-1">{tradingName}</Body>
              </div>
              <div className="flex flex-column mb-4">
                <LegalBody>{i18next.t('collateralDueDate')}</LegalBody>
                <Body className="mt-1">{date.format({ date: collateralDueDate })}</Body>
              </div>
              {amountOfOpportunities > 1 && <Divider />}
            </div>
          ))
        : null}
      {contractStatus !== ContractStatus.WAITING_DRAFT && (
        <>
          <div className="flex flex-column mb-4">
            <LegalBody>{i18next.t('registrationNumber')}</LegalBody>
            <Body className="mt-1">{draftRegistrationNumber || ' - '}</Body>
          </div>
          <div className="flex flex-column">
            <LegalBody>{i18next.t('commentsOrRecommendations')}</LegalBody>
            <Body className="mt-1 break-line">{draftInstructions || ' - '}</Body>
          </div>
        </>
      )}
      <div className="flex flex-column mt-4">
        {draftFiles?.map(file => (
          <div key={file.id} className="flex flex-column">
            <TinyTitle className="mt-1 text-transform-capitalize">
              {documentRequestTypesTranslations[file.documentType]}
            </TinyTitle>
            <DocumentCard document={file} />
          </div>
        ))}
      </div>
      {editable && !!selectedFiles?.length && (
        <div className="flex flex-column">
          {selectedFiles.map(file => (
            <div className="flex align-items-center" key={file['newFileId']}>
              <StyledSelect
                key={file['newFileId']}
                sizeVariant="slim"
                label={i18next.t('documentType')}
                name="documentType"
                options={documentRequestTypesOptions}
                value={documentTypeDictionary[file['newFileId']]}
                onSelectionChanged={handleUpdateDocumentType(file['newFileId'])}
                placeholder={i18next.t('pleaseSelect')}
                error={!documentTypeDictionary[file['newFileId']]}
              />
              <div style={{ maxWidth: 'calc(100% - 200px)' }}>
                <UploadDocumentCard
                  documentName={file.name}
                  progress={progress ? progress[file['newFileId']] : undefined}
                  onRemove={() => handleFileRemove(file['newFileId'])}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {editable && (
        <div className="flex flex-space-btw">
          <AttachButton
            onFilesSelected={handleFilesSelected}
            size="small"
            faIconAfter={faCloudUpload}
            variant="tertiary"
          >
            {i18next.t('uploadFiles')}
          </AttachButton>
          <Button size="small" disabled={isSendDraftDisabled} onClick={handleSendDraft} busy={sendingDraft}>
            {i18next.t('sendDraft')}
          </Button>
        </div>
      )}
      {draftSentBy && (
        <LegalBody className="mt-4">
          {UserUtil.getUserFullName(draftSentBy)}
          {draftSentAt ? ' - ' + utils.date.formatDate(draftSentAt) : null}
        </LegalBody>
      )}
      {!!agFinance?.guaranteeData ? (
        <>
          <Divider className="mt-4 mb-4" />
          <LargeTitle className="text-transform-capitalize">{i18next.t('guaranteeData')}</LargeTitle>
          <GuaranteeData />
        </>
      ) : null}
    </PhaseSection>
  );
};
